<template>
  <BaseModule
    v-bind="$props"
    data-module="banner-with-menu"
    class="banner-with-menu"
    :class="{
      'no-banners': banners.length === 0 || !hasActiveBanner,
      transparent: opacity < 1 || useGradient,
    }"
    :style="{
      '--header-height': `${opacity < 1 || useGradient ? headerHeight : 0}px`,
    }"
  >
    <Header
      ref="header"
      :buttons="buttons"
      :background-color="backgroundColor"
      :button-color="buttonColor"
      :button-text-color="buttonTextColor"
      :opacity="opacity"
      :use-gradient="useGradient"
      :links-position="linksPosition"
      :text-color="textColor"
      :menu-links="menuLinks"
      :instance-id="instanceId"
      :logo-url="logoUrl"
      :has-logo-link="hasLogoLink"
      :logo-img="logoImg"
      :logo-alt-text="logoAltText"
    />
    <Banner
      v-if="banners.length || useFixedText"
      :title-size-desk="titleSizeDesk"
      :title-size-mobile="titleSizeMobile"
      :content-size-desk="contentSizeDesk"
      :content-size-mobile="contentSizeMobile"
      :banners="banners"
      :use-fixed-text="useFixedText"
      :use-max-height="useMaxHeight"
      :max-height="maxHeight"
      :swiper-autoplay="swiperAutoplay"
      :swiper-autoplay-speed="swiperAutoplaySpeed"
      v-bind="fixedBanner"
      :instance-id="instanceId"
    >
      <slot></slot>
    </Banner>
  </BaseModule>
</template>

<script>
import debounce from 'lodash/debounce';

import Header from './Header.vue';
import Banner from './Banner.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'BannerWithMenu',
  components: {
    BaseModule,
    Header,
    Banner,
  },
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    ...Header.props,
    ...Banner.props,
    textColor: {
      type: String,
      default: '#fff', // overrides default
    },
  },
  data() {
    return {
      headerHeight: 90,
    };
  },
  computed: {
    // TODO: Improve this v-bind and the whole props system
    // Use something like typescript and dicts so we can pass
    // objects as props all the way
    fixedBanner() {
      const {
        backgroundColor,
        topSubtitle,
        title,
        titleWeight,
        subtitle,
        buttonText,
        linkUrl,
        textPosition,
        image,
        imageAltText,
        applyImageFilter,
        imageFilterBlur,
      } = this;

      return {
        backgroundColor,
        topSubtitle,
        title,
        titleWeight,
        subtitle,
        buttonText,
        linkUrl,
        textPosition,
        image,
        imageAltText,
        applyImageFilter,
        imageFilterBlur,
      };
    },
    hasActiveBanner() {
      return this.banners.some((banner) =>
        Banner.methods.isBannerActive(banner),
      );
    },
  },
  mounted() {
    window.addEventListener('load', debounce(this.updateHeaderHeight, 150));
    window.addEventListener('resize', debounce(this.updateHeaderHeight, 150));
  },
  methods: {
    updateHeaderHeight() {
      if (this.$refs.header?.$el) {
        const { height } = this.$refs.header.$el.getBoundingClientRect();
        this.headerHeight = height;
      }
    },
  },
  metadata: {
    extends: Banner.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'header',
            label: 'Cabeçalho',
            position: 0,
            fields: [
              {
                name: 'logoImg',
                type: 'ImageFile',
                label: 'Logo',
                maxWidth: 300,
                maxHeight: 90,
                resizeInsteadCrop: true,
                helpText: 'Tamanho máximo 300x90, formato .PNG',
                enableRemoval: true,
              },
              {
                name: 'hasLogoLink',
                type: 'Checkbox',
                label: 'Adicionar link no logo',
              },
              {
                name: 'logoUrl',
                type: 'Url',
                label: 'Link do logo',
                isEnabled({ hasLogoLink }) {
                  return hasLogoLink;
                },
              },
              {
                name: 'logoAltText',
                type: 'ShortText',
                label: 'Texto alternativo',
                helpText:
                  'O texto será usado como descrição alternativa (Alt) para a imagem e aparecerá caso não haja um logo definido',
              },
              {
                name: 'menuLinks',
                type: 'MenuSelect',
                label: 'Tipo de menu',
                helpText:
                  'Abaixo você poderá escolher entre usar um menu customizado, exclusivo para este módulo, ou usar um menu global reutilizável através de módulos e páginas',
                customMenuLabel: 'Itens do menu',
                customMenuHelpText:
                  'Reorganize os itens do menu os arrastando. Crie um submenu movendo um item para dentro de outro.',
              },
              {
                name: 'linksPosition',
                type: 'VueSelect',
                label: 'Posição dos menus',
                default: 'Direita',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'Esquerda',
                  },
                  {
                    label: 'Direita',
                    value: 'Direita',
                  },
                  {
                    label: 'Centro',
                    value: 'Centro',
                  },
                ],
              },
              {
                name: 'buttons',
                type: 'List',
                maxItems: 1,
                label: 'Botões',
                itemLabel: 'Botão {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar botão',
                fields: [
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 30,
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                  },
                ],
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'header',
            label: 'Cabeçalho',
            position: 0,
            fields: [
              {
                name: 'backgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo',
              },
              {
                name: 'opacity',
                type: 'Range',
                label: 'Opacidade',
                min: 0,
                max: 1,
                step: 0.1,
                format: {
                  decimals: 1,
                },
              },
              {
                name: 'useGradient',
                type: 'Checkbox',
                label: 'Usar gradiente',
              },
              {
                name: 'textColor',
                type: 'ColorPicker',
                label: 'Cor dos links',
              },
              {
                name: 'buttonColor',
                type: 'ColorPicker',
                label: 'Cor do botão',
              },
              {
                name: 'buttonTextColor',
                type: 'ColorPicker',
                label: 'Cor de texto botão',
              },
            ],
          },
        ],
        toRemove: ['transitions'],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.banner-with-menu {
  position: relative;
  background: $lb-white;

  &::v-deep {
    .banner {
      padding-top: var(--header-height);
    }
  }
}
</style>
